/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React from 'react';

export interface PrimeTablePaginatorProps {
  paginator: boolean;
  first: number;
  currentRows: number;
  recordCount: number;
  currentPage: number;
  pageCount: number;
  handlePageChange: (newFirst: number, newRows?: number) => void;
  rowsPerPageOptions: number[];
}

export const PrimeTablePaginator: React.FC<PrimeTablePaginatorProps> = ({
  paginator,
  first,
  currentRows,
  recordCount,
  currentPage,
  pageCount,
  handlePageChange,
  rowsPerPageOptions,
}) => {
  if (!paginator) return null;

  const pages: number[] = [];
  const maxPages = 5;
  let startPage = Math.max(0, currentPage - Math.floor(maxPages / 2));
  const endPage = Math.min(pageCount, startPage + maxPages);

  if (endPage - startPage < maxPages) {
    startPage = Math.max(0, endPage - maxPages);
  }

  for (let i = startPage; i < endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="prime-table-paginator">
      <div className="paginator-info">
        Showing {first + 1} to {Math.min(first + currentRows, recordCount)} of {recordCount}{' '}
        entries
      </div>
      <div className="paginator-controls">
        <button
          className="paginator-button"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(0)}
        >
          First
        </button>
        <button
          className="paginator-button"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(first - currentRows)}
        >
          Previous
        </button>
        <div className="paginator-pages">
          {pages.map((page) => (
            <button
              key={page}
              className={`paginator-button ${page === currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page * currentRows)}
            >
              {page + 1}
            </button>
          ))}
        </div>
        <button
          className="paginator-button"
          disabled={currentPage >= pageCount - 1}
          onClick={() => handlePageChange(first + currentRows)}
        >
          Next
        </button>
        <button
          className="paginator-button"
          disabled={currentPage >= pageCount - 1}
          onClick={() => handlePageChange((pageCount - 1) * currentRows)}
        >
          Last
        </button>
      </div>
      <div className="rows-per-page">
        <span>Rows per page:</span>
        <select
          value={currentRows}
          onChange={(e) => handlePageChange(0, parseInt(e.target.value))}
        >
          {rowsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default PrimeTablePaginator;
